import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';

interface AdvanceClaim {
  id: string;
  claim_number: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  amount: number;
  status: string;
  purpose: string;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search
    ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase()))
    : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const fetchApprovedClaimOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/ecs/claims', { params: { status: 'APPROVED' } });
  const claims = res.data.claims || [];
  const filtered = search
    ? claims.filter((c: any) => c.claim_title?.toLowerCase().includes(search.toLowerCase()))
    : claims;
  return filtered.map((c: any) => ({
    value: c.id,
    label: `${c.claim_number} — ${c.claim_title} ($${Number(c.total_amount).toLocaleString()})`,
  }));
};

export default function AdvanceClaims() {
  const [advances, setAdvances] = useState<AdvanceClaim[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [settleTarget, setSettleTarget] = useState<AdvanceClaim | null>(null);
  const [settleClaimId, setSettleClaimId] = useState('');
  const [formData, setFormData] = useState({
    employeeId: '',
    purpose: '',
    amount: 0,
  });

  useEffect(() => {
    fetchAdvances();
  }, []);

  const fetchAdvances = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecs/advance-claims');
      setAdvances(response.data.advances || []);
    } catch (error) {
      console.error('Failed to fetch advance claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/ecs/advance-claims', formData);
      setFormData({ employeeId: '', purpose: '', amount: 0 });
      setShowForm(false);
      fetchAdvances();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create advance claim');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await api.put(`/ecs/advance-claims/${id}/approve`);
      fetchAdvances();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to approve advance');
    }
  };

  const handleDisburse = async (id: string) => {
    try {
      await api.put(`/ecs/advance-claims/${id}/disburse`);
      fetchAdvances();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to disburse advance');
    }
  };

  const handleSettle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settleTarget || !settleClaimId) return;
    try {
      await api.put(`/ecs/advance-claims/${settleTarget.id}/settle`, {
        settledAgainstClaimId: settleClaimId,
      });
      setSettleTarget(null);
      setSettleClaimId('');
      fetchAdvances();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to settle advance');
    }
  };

  const columns: Column<AdvanceClaim>[] = [
    { key: 'claim_number', header: 'Claim No.' },
    { key: 'employee', header: 'Employee', render: (row) => `${row.first_name} ${row.last_name}` },
    { key: 'purpose', header: 'Purpose' },
    { key: 'amount', header: 'Amount', render: (row) => `$${Number(row.amount).toLocaleString()}` },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          {row.status === 'REQUESTED' && (
            <button onClick={() => handleApprove(row.id)} className="text-green-700 hover:underline text-sm">
              Approve
            </button>
          )}
          {row.status === 'APPROVED' && (
            <button onClick={() => handleDisburse(row.id)} className="text-blue-600 hover:underline text-sm">
              Disburse
            </button>
          )}
          {row.status === 'DISBURSED' && (
            <button onClick={() => setSettleTarget(row)} className="text-purple-600 hover:underline text-sm">
              Settle
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Advance Claims</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Request Advance'}</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <LookupSelect
                label="Employee"
                value={formData.employeeId}
                onChange={(value) => setFormData({ ...formData, employeeId: value })}
                fetchOptions={fetchEmployeeOptions}
                placeholder="Search employee..."
                required
              />
              <Input
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                required
              />
              <textarea
                placeholder="Purpose of Advance"
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                className="px-4 py-2 border rounded-lg col-span-2"
                rows={3}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Request Advance
            </Button>
          </form>
        </div>
      )}

      <Table columns={columns} rows={advances} loading={loading} emptyMessage="No advance claims yet" />

      <Modal isOpen={!!settleTarget} onClose={() => setSettleTarget(null)} title="Settle Advance">
        <form onSubmit={handleSettle} className="space-y-4">
          <p className="text-sm text-gray-600">
            Settling advance {settleTarget?.claim_number} (${Number(settleTarget?.amount || 0).toLocaleString()})
            against an approved expense claim. Any shortfall is tracked as unrecovered.
          </p>
          <LookupSelect
            label="Approved Claim"
            value={settleClaimId}
            onChange={(value) => setSettleClaimId(value)}
            fetchOptions={fetchApprovedClaimOptions}
            placeholder="Search approved claims..."
            required
          />
          <Button type="submit" className="w-full">
            Settle
          </Button>
        </form>
      </Modal>
    </div>
  );
}
